import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EuroIcon from "@mui/icons-material/Euro";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import WeekendIcon from "@mui/icons-material/Weekend";
import CelebrationIcon from "@mui/icons-material/Celebration";
import pricingImage from "../assets/images/weekendopvang.jpg"; // Using an existing image from your assets

// Styled Components
const StyledPricingSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0),
  backgroundColor: theme.palette.background.default,
}));

const StyledPricingSubtitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(6),
  maxWidth: "800px",
  marginLeft: "auto",
  marginRight: "auto",
}));

const StyledPricingImage = styled('img')<{ $isMobile: boolean }>(({ theme, $isMobile }) => ({
  width: "100%",
  height: $isMobile ? "300px" : "400px",
  objectFit: "cover",
  borderRadius: theme.spacing(1),
  boxShadow: theme.shadows[3],
}));

const StyledPricingPaper = styled(Paper)<{ $isDarkMode: boolean }>(({ theme, $isDarkMode }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(1),
  backgroundColor: $isDarkMode ? "#3D3D3D" : theme.palette.background.paper,
  color: $isDarkMode ? "#F0F0F0" : theme.palette.text.primary,
}));

const StyledPricingListItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(2, 0),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledNoteBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.spacing(0.5),
}));

const StyledLastPricingListItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(2, 0),
  borderBottom: 'none',
}));

export const PricingSection: React.FC = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <StyledPricingSection
      id="pricing"
      component="section"
      aria-labelledby="pricing-heading"
    >
      <Container maxWidth="lg">
        <Typography
          variant="h3"
          component="h2"
          align="center"
          gutterBottom
          id="pricing-heading"
          color="primary.main"
        >
          Onze Tarieven
        </Typography>

        <StyledPricingSubtitle
          variant="body1"
          align="center"
        >
          Duidelijke tarieven voor dagopvang, logeren en feestdagen. Zo weet je
          vooraf precies waar je aan toe bent.
        </StyledPricingSubtitle>

        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <StyledPricingImage
              src={pricingImage}
              alt="Huisdier geniet van opvang bij Pippaya"
              $isMobile={isMobile}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <StyledPricingPaper elevation={3} $isDarkMode={isDarkMode}>
              <Typography
                variant="h5"
                component="h3"
                gutterBottom
                color="secondary.main"
              >
                Tarieven Overzicht
              </Typography>

              <List aria-label="Prijslijst voor hondenopvang">
                <StyledPricingListItem>
                  <ListItemIcon>
                    <EuroIcon color="secondary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Dagopvang"
                    secondary="Brengen vanaf 08:00 uur en ophalen vóór 18:00 uur"
                  />
                  <Typography
                    variant="h6"
                    color="primary.main"
                    fontWeight="bold"
                  >
                    €37,50
                  </Typography>
                </StyledPricingListItem>

                <StyledPricingListItem>
                  <ListItemIcon>
                    <WeekendIcon color="secondary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="1 t/m 5 nachten"
                    secondary="Brengen na 14:00 uur en ophalen vóór 10:00 uur"
                  />
                  <Typography
                    variant="h6"
                    color="primary.main"
                    fontWeight="bold"
                  >
                    €40,00
                  </Typography>
                </StyledPricingListItem>

                <StyledPricingListItem>
                  <ListItemIcon>
                    <CalendarMonthIcon color="secondary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="6 nachten of meer"
                    secondary="Brengen na 14:00 uur en ophalen vóór 10:00 uur"
                  />
                  <Typography
                    variant="h6"
                    color="primary.main"
                    fontWeight="bold"
                  >
                    €35,00
                  </Typography>
                </StyledPricingListItem>
                <StyledPricingListItem>
                  <ListItemIcon>
                    <CelebrationIcon color="secondary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Feestdagopvang"
                    secondary="Brengen vanaf 10:00 uur. Eventueel inclusief één overnachting; ophalen vóór 10:00 uur de volgende ochtend."
                  />
                  <Typography
                    variant="h6"
                    color="primary.main"
                    fontWeight="bold"
                  >
                    €50,00
                  </Typography>
                </StyledPricingListItem>

                <StyledLastPricingListItem>
                  <ListItemIcon>
                    <EuroIcon color="secondary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Afwijkende breng- en ophaaltijden"
                    secondary="Alleen mogelijk in overleg en wanneer deze vooraf bij de boeking zijn afgesproken"
                  />
                  <Typography
                    variant="h6"
                    color="primary.main"
                    fontWeight="bold"
                  >
                    €5,00 per extra uur
                  </Typography>
                </StyledLastPricingListItem>
              </List>

              <StyledNoteBox>
                <Typography variant="body2" color="text.secondary">
                  <strong>Goed om te weten:</strong> Voor logeren geldt dat een hond
                  op de eerste gereserveerde dag na 14:00 uur kan worden gebracht
                  en op de laatste gereserveerde dag vóór 10:00 uur wordt opgehaald.
                  <br /><br />
                  Afwijkende breng- of ophaaltijden zijn alleen mogelijk in overleg
                  en wanneer dit vooraf bij de boeking is afgesproken. Hiervoor geldt
                  €5,00 per extra uur. Bij onaangekondigd later ophalen kan de extra
                  tijd eveneens in rekening worden gebracht.
                  <br /><br />
                  De eigenaar brengt voor een verblijf voldoende eigen voeding mee,
                  zodat de hond het vertrouwde voer kan blijven eten. Normale medicatie
                  die eenvoudig met de maaltijd of op een afgesproken tijd kan worden
                  gegeven, is inbegrepen. Voor intensieve of aanvullende verzorging
                  kunnen in overleg extra kosten gelden.
                </Typography>
              </StyledNoteBox>
            </StyledPricingPaper>
          </Grid>
        </Grid>
      </Container>
    </StyledPricingSection>
  );
};
